import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Badge, Alert } from '@dreamtree-org/twreact-ui';
import { CheckCircle2, XCircle, Clock, FileText } from 'lucide-react';
import { PageHeader, SectionCard, StatusBadge, QueryState, EmptyState, KeyValue, StatCard } from '@/components';
import { formatDate } from '@/lib/format';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ReasonDialog from '../components/ReasonDialog';
import Trail from '../components/Trail';
import { useQueue } from '../hooks/useQueue';
import { filterRows, optionsWithCounts, countBy } from '../lib/filters';
import { availableActions, payoutReadiness } from '../lib/decisions';
import { humanize } from '../lib/format';

// What approval turns on for each applied-for seller kind (D57; server: lib/platform/capabilities.js KINDS).
const KIND_GRANTS = { store: null, vendor: 'Vendor', dropshipper: 'Dropshipper', hybrid: 'Vendor and Dropshipper' };
const grantHint = (app) => (KIND_GRANTS[app.kind] ? `Approving also turns on the ${KIND_GRANTS[app.kind]} capability for this store (cross-store supply).` : undefined);

const DOC_ICON = { verified: CheckCircle2, rejected: XCircle, pending: Clock };
const OPEN = ['submitted', 'in_review', 'info_requested'];

/** ADMIN-01 Seller onboarding & KYC review (real since D8a: applications come from the public sign-up, D17). */
export default function Onboarding() {
  const [params, setParams] = useSearchParams();
  const { query, rows, mutate } = useQueue('platformOnboarding');
  const [q, setQ] = useState('');
  const status = params.get('status') || 'open';
  const selectedId = params.get('id');
  const [action, setAction] = useState(null);
  const [docAction, setDocAction] = useState(null);
  const [msg, setMsg] = useState(null);

  const select = (id) => {
    const next = new URLSearchParams(params);
    next.set('id', id);
    setParams(next, { replace: true });
  };
  const setStatus = (_, v) => {
    const next = new URLSearchParams(params);
    next.set('status', v);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Seller onboarding" subtitle="ADMIN-01 · KYC evidence, decisions, bank and payout readiness" />
      <QueryState query={query}>
        {() => {
          const counts = countBy(rows, 'status');
          const visible = status === 'open' ? rows.filter((r) => OPEN.includes(r.status)) : filterRows(rows, { status });
          const list = filterRows(visible, {}, q, ['business_name', 'legal_name', 'owner_name', 'area', 'id', 'gstin']);
          const app = rows.find((r) => r.id === selectedId) || list[0] || null;
          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Awaiting review" value={(counts.submitted || 0) + (counts.in_review || 0)} hint="submitted or in review" />
                <StatCard label="Info requested" value={counts.info_requested || 0} hint="waiting on applicant" />
                <StatCard label="Approved sellers" value={counts.approved || 0} />
                <StatCard label="Paused" value={counts.paused || 0} hint="with reason on record" />
              </div>
              {msg ? <Alert variant="success" dismissible onDismiss={() => setMsg(null)}><span className="break-all">{msg}</span></Alert> : null}
              <div className="grid gap-4 xl:grid-cols-5">
                <SectionCard title="Applications" className="xl:col-span-2">
                  <FilterBar
                    compact
                    q={q}
                    onQ={setQ}
                    placeholder="Business, owner, GSTIN…"
                    filters={[{ key: 'status', label: 'Queue', value: status, options: [{ value: 'open', label: `Open (${rows.filter((r) => OPEN.includes(r.status)).length})` }, ...optionsWithCounts(rows, 'status')] }]}
                    onFilter={setStatus}
                  />
                  {list.length ? (
                    <DataTable
                      data={list}
                      onRowClick={(r) => select(r.id)}
                      rowClass={({ row }) => (app && row.id === app.id ? 'bg-primary-50' : '')}
                      columns={[
                        { key: 'business_name', label: 'Applicant', render: (r) => (
                          <div>
                            <div className="font-medium">{r.business_name}</div>
                            <div className="text-xs opacity-60">{r.id} · {r.area} · {formatDate(r.submitted_at)}</div>
                          </div>
                        ) },
                        { key: 'status', label: 'Status', render: (r) => (
                          <div className="space-y-1">
                            <StatusBadge status={r.status} />
                            <div><Badge variant={r.risk_score > 50 ? 'error' : r.risk_score > 30 ? 'warning' : 'success'} size="xs">risk {r.risk_score}</Badge></div>
                          </div>
                        ) },
                      ]}
                    />
                  ) : <EmptyState title="Nothing in this queue">Pick another queue or clear the search.</EmptyState>}
                </SectionCard>

                <div className="xl:col-span-3 space-y-4">
                  {app ? (
                    <ApplicationDetail
                      app={app}
                      onAction={setAction}
                      onDoc={(doc, to) => setDocAction({ doc, to })}
                    />
                  ) : <EmptyState title="Select an application" />}
                </div>
              </div>

              {app ? (
                <ReasonDialog
                  action={action}
                  subject={app.business_name}
                  onClose={() => setAction(null)}
                  reasonHint={action && action.key === 'pause' ? 'Paused sellers stop receiving orders; their listings are hidden until restored.' : action && action.key === 'approve' ? grantHint(app) : undefined}
                  onConfirm={async ({ reason }) => {
                    const row = await mutate('platformOnboardingDecide', { id: app.id, action: action.key, reason });
                    setMsg(row.owner_join_url
                      ? `${app.business_name} approved: store provisioned. The owner was sent a join link (also shown once here): ${row.owner_join_url}`
                      : `${app.business_name}: ${humanize(row.status)}`);
                  }}
                />
              ) : null}
              {app && docAction ? (
                <ReasonDialog
                  action={{ key: docAction.to, label: docAction.to === 'verified' ? 'Verify document' : 'Reject document', variant: docAction.to === 'verified' ? 'success' : 'destructive', reasonRequired: docAction.to === 'rejected' }}
                  subject={docAction.doc.label}
                  onClose={() => setDocAction(null)}
                  onConfirm={async ({ reason }) => {
                    await mutate('platformOnboardingDocument', { id: app.id, type: docAction.doc.type, status: docAction.to === 'failed' ? 'rejected' : docAction.to, note: reason });
                    setMsg(`${docAction.doc.label} ${docAction.to}`);
                  }}
                />
              ) : null}
            </>
          );
        }}
      </QueryState>
    </div>
  );
}

function ApplicationDetail({ app, onAction, onDoc }) {
  const actions = availableActions('onboarding', app);
  const payout = payoutReadiness(app);
  const reviewable = OPEN.includes(app.status);
  return (
    <>
      <SectionCard
        title={(
          <span className="flex flex-wrap items-center gap-2">{app.business_name} <StatusBadge status={app.status} /></span>
        )}
        actions={(
          <div className="flex flex-wrap gap-2">
            {actions.map((a) => <Button key={a.key} size="sm" variant={a.variant} onClick={() => onAction(a)}>{a.label}</Button>)}
          </div>
        )}
      >
        <KeyValue columns={2} items={[
          { label: 'Application', value: app.id },
          { label: 'Legal name', value: app.legal_name },
          { label: 'Owner', value: app.owner_name },
          { label: 'Contact', value: `${app.email} · ${app.phone}` },
          { label: 'GSTIN', value: <code className="text-xs">{app.gstin}</code> },
          { label: 'PAN', value: <code className="text-xs">{app.pan_masked}</code> },
          { label: 'Area', value: [app.area, app.city].filter(Boolean).join(', ') },
          { label: 'Expected stock', value: `${app.expected_units} units` },
          { label: 'Store', value: app.store_slug ? <code className="text-xs">{app.store_slug}</code> : '—' },
          { label: 'Plan', value: app.plan_key },
          { label: 'Seller kind', value: KIND_GRANTS[app.kind] ? <Badge variant="warning" size="sm">{KIND_GRANTS[app.kind]} capability on approval</Badge> : 'Own store' },
          // What they sell (MERCH-89): approved with the store, its types turned on; older applications have free-text categories.
          {
            label: 'Sells',
            value: (app.industries || []).length
              ? (app.industries || []).map((i) => `${i.label}: ${(app.product_types || []).filter((t) => t.industry === i.key).map((t) => t.label).join(', ')}`).join(' · ')
              : <span>{(app.categories || []).join(', ') || '—'} <Badge variant="warning" size="sm">No industries: approve them on the merchant page after approval</Badge></span>,
          },
          { label: 'Agreement', value: app.agreement_signed ? `Signed (${app.agreement_version})` : 'Not signed' },
        ]} />
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="opacity-60">Service PIN codes:</span>
          {app.service_regions.map((p) => <Badge key={p} variant="outline" size="sm">{p}</Badge>)}
        </div>
      </SectionCard>

      <SectionCard title="KYC evidence">
        <ul className="divide-y divide-[var(--app-border)]">
          {app.documents.map((d) => {
            const Icon = DOC_ICON[d.status] || FileText;
            return (
              <li key={d.type} className="py-3 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <Icon size={20} className={d.status === 'verified' ? 'text-success-600' : d.status === 'rejected' ? 'text-error-600' : 'text-warning-600'} aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="font-medium">{d.label}</div>
                    <div className="text-xs opacity-60">
                      {d.status === 'missing' ? (d.required ? 'Not uploaded yet' : 'Optional · not uploaded') : (
                        <>
                          <a className="underline" href={`/api/platform/onboarding/${app.id}/documents/${d.type}/file`} target="_blank" rel="noreferrer">{d.file || 'View file'}</a> · uploaded {formatDate(d.uploaded_at)}
                        </>
                      )}
                    </div>
                    {d.checked_by ? <div className="text-xs opacity-60">Reviewed by {d.checked_by}</div> : null}
                    {d.note ? <div className="text-sm text-error-600">{d.note}</div> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={d.status} />
                  {reviewable && d.status !== 'missing' && d.status !== 'verified' ? <Button size="xs" variant="success" onClick={() => onDoc(d, 'verified')}>Verify</Button> : null}
                  {reviewable && d.status !== 'missing' && d.status !== 'rejected' ? <Button size="xs" variant="outline" onClick={() => onDoc(d, 'rejected')}>Reject</Button> : null}
                </div>
              </li>
            );
          })}
        </ul>
      </SectionCard>

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Bank & payout readiness" actions={<Badge variant={payout.ready ? 'success' : 'warning'}>{payout.ready ? 'Payout ready' : 'Not ready'}</Badge>}>
          <KeyValue items={[
            { label: 'Bank', value: app.bank.bank },
            { label: 'Account', value: <code className="text-xs">{app.bank.account_masked}</code> },
            { label: 'IFSC', value: app.bank.ifsc },
            { label: 'Holder', value: app.bank.holder },
            { label: 'Check', value: <span className="flex items-center gap-2 justify-end">{app.bank.method} <StatusBadge status={app.bank.status} /></span> },
            app.bank.note ? { label: 'Note', value: app.bank.note } : null,
          ]} />
          {reviewable ? (
            <div className="flex flex-wrap gap-2">
              {app.bank.status !== 'verified' ? <Button size="xs" variant="success" onClick={() => onDoc({ type: 'bank', label: 'Bank account' }, 'verified')}>Bank verified</Button> : null}
              {app.bank.status !== 'failed' ? <Button size="xs" variant="outline" onClick={() => onDoc({ type: 'bank', label: 'Bank account' }, 'rejected')}>Bank mismatch</Button> : null}
            </div>
          ) : null}
          <ul className="space-y-1 text-sm pt-2">
            {payout.checks.map((c) => (
              <li key={c.key} className="flex items-center gap-2">
                {c.ok ? <CheckCircle2 size={16} className="text-success-600" aria-hidden="true" /> : <XCircle size={16} className="text-error-600" aria-hidden="true" />}
                <span>{c.label}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
        <SectionCard title="Decision trail">
          <Trail entries={app.trail} />
        </SectionCard>
      </div>
    </>
  );
}
